import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl } from '../../utils/apiClient';
import { csrfFetch } from '../../utils/csrfFetch';
import { getNavigationUrl } from '../../utils/subdomainUtils';
import RedirectService from '../../services/RedirectService';
import { isPeopleProfileSelfCreateUiEnabled } from '../../../shared/people/peopleProfileGrowthFlags';
import {
  buildPeopleProfileFinishPath,
  derivePeopleProfileCtaState,
  type PeopleProfileCtaSurface,
} from '../../../shared/people/peopleProfileCtaState';
import type { PersonCapabilityKey } from '../../../shared/people/personAuthorBridgeFields';
import { logger } from '../../utils/logger';

const PEOPLE_PROFILE_RETURN_KEY = 'people_profile_return';

export type PeopleProfileCtaProps = {
  surface: PeopleProfileCtaSurface;
  /** When on a public profile page, the slug being viewed. */
  viewingSlug?: string | null;
  /** Optional override for finish return path (defaults to current location). */
  returnPath?: string;
  onOpenSignupIntent?: (redirectUrl: string) => void;
};

type MeSnapshot = {
  slug: string;
  verification_status: string;
  is_public: boolean;
  publicly_visible?: boolean;
};

const CAPABILITY_OPTIONS: { value: PersonCapabilityKey; label: string; hint: string }[] = [
  {
    value: 'professional',
    label: 'Professional / SME',
    hint: 'Show up as a verified professional in the People directory.',
  },
  {
    value: 'author',
    label: 'Author',
    hint: 'Write and publish under your name (additional unlock steps may apply).',
  },
  {
    value: 'instructor',
    label: 'Instructor',
    hint: 'Teaching presence; public Teaching still follows bio approval rules.',
  },
];

const PeopleProfileCta: React.FC<PeopleProfileCtaProps> = ({
  surface,
  viewingSlug = null,
  returnPath,
  onOpenSignupIntent,
}) => {
  const uiEnabled = isPeopleProfileSelfCreateUiEnabled(
    import.meta.env.VITE_PEOPLE_PROFILE_SELF_CREATE_ENABLED as string | undefined,
  );
  const { isAuthenticated, isInitializing } = useAuth();
  const location = useLocation();
  const [profile, setProfile] = useState<MeSnapshot | null | undefined>(undefined);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [capability, setCapability] = useState<PersonCapabilityKey>('professional');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [finishBanner, setFinishBanner] = useState(false);

  const finishPath = useMemo(() => {
    if (returnPath) return buildPeopleProfileFinishPath(returnPath.split('?')[0] || returnPath);
    return buildPeopleProfileFinishPath(location.pathname || '/people');
  }, [location.pathname, returnPath]);

  const loadMe = useCallback(async () => {
    if (!isAuthenticated) {
      setProfile(null);
      return;
    }
    try {
      const res = await fetch(getApiUrl('/api/people/me'), { credentials: 'include' });
      if (res.status === 404) {
        setProfile(null);
        return;
      }
      const json = (await res.json()) as {
        success?: boolean;
        data?: MeSnapshot;
      };
      if (res.ok && json.success !== false && json.data) {
        setProfile({
          slug: json.data.slug,
          verification_status: json.data.verification_status,
          is_public: json.data.is_public,
          publicly_visible: json.data.publicly_visible,
        });
      } else {
        setProfile(null);
      }
    } catch (e) {
      logger.error('PeopleProfileCta: failed to load /me', e, {});
      setProfile(null);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isInitializing) return;
    void loadMe();
  }, [isInitializing, loadMe]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('people_profile') === 'finish') {
      setFinishBanner(true);
    }
  }, [location.search]);

  const derived = useMemo(
    () =>
      derivePeopleProfileCtaState({
        isAuthenticated: Boolean(isAuthenticated),
        profile: profile ?? null,
        viewingSlug,
      }),
    [isAuthenticated, profile, viewingSlug],
  );

  if (!uiEnabled || isInitializing || profile === undefined) {
    return null;
  }

  const startGuestSignup = () => {
    try {
      sessionStorage.setItem(PEOPLE_PROFILE_RETURN_KEY, finishPath);
    } catch {
      /* ignore */
    }
    RedirectService.storeRedirectUrl(finishPath);
    if (onOpenSignupIntent) {
      onOpenSignupIntent(finishPath);
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.set('signup', '1');
    url.searchParams.set('people_profile', 'finish');
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
    window.dispatchEvent(new CustomEvent('apt:open-signup-intent', { detail: { redirectUrl: finishPath } }));
  };

  const onPrimary = () => {
    setError(null);
    if (!isAuthenticated) {
      startGuestSignup();
      return;
    }
    if (derived.primary === 'create') {
      setPickerOpen(true);
      return;
    }
    if (derived.primary === 'finish') {
      window.location.href = getNavigationUrl(derived.managePath);
      return;
    }
    if (derived.primary === 'view' && derived.viewPath) {
      window.location.href = getNavigationUrl(derived.viewPath);
    }
  };

  const submitCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      const res = await csrfFetch(getApiUrl('/api/people/me'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ capability }),
      });
      const json = (await res.json()) as { success?: boolean; error?: string };
      if (!res.ok || json.success === false) {
        setError(json.error || 'Could not create profile');
        return;
      }
      setPickerOpen(false);
      setFinishBanner(false);
      await loadMe();
      window.location.href = getNavigationUrl('/dashboard/settings?tab=profile');
    } catch (e) {
      logger.error('PeopleProfileCta: create failed', e, {});
      setError('Could not create profile. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const primaryLabel =
    derived.primary === 'create'
      ? 'Create your public profile'
      : derived.primary === 'finish'
        ? 'Finish your profile'
        : derived.primary === 'view'
          ? 'View your profile'
          : null;

  const intro =
    surface === 'zero_results'
      ? 'No people matched your search. Create your own public profile to be discoverable here.'
      : surface === 'profile'
        ? 'Professionals on American Police Training share a public profile. Start yours in a few steps.'
        : 'Build a public profile to be listed with verified professionals, authors, and instructors.';

  const showFinishBanner = finishBanner && isAuthenticated && !profile;

  return (
    <Box
      sx={{
        mb: surface === 'zero_results' ? 0 : 3,
        p: 2,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
      data-testid={`people-profile-cta-${surface}`}
    >
      {showFinishBanner ? (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          onClose={() => setFinishBanner(false)}
          action={
            <Button color="inherit" size="small" onClick={() => setPickerOpen(true)}>
              Create profile
            </Button>
          }
        >
          You&apos;re signed in — finish by creating your People profile.
        </Alert>
      ) : null}

      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        {surface === 'zero_results' ? 'Create your profile' : 'Your public profile'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {intro} Managing a profile requires an account; choose any account type when you sign up.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
        {primaryLabel ? (
          <Button variant="contained" onClick={onPrimary} data-testid="people-profile-cta-primary">
            {primaryLabel}
          </Button>
        ) : null}
        {!isAuthenticated ? (
          <Button
            variant="outlined"
            component={RouterLink}
            to={`/login?redirect=${encodeURIComponent(finishPath)}`}
          >
            Log in
          </Button>
        ) : null}
        {derived.showSoftManage ? (
          <Button
            variant="text"
            component={RouterLink}
            to={derived.managePath}
            data-testid="people-profile-cta-manage"
          >
            Manage your public profile
          </Button>
        ) : null}
      </Stack>

      <Dialog open={pickerOpen} onClose={() => !creating && setPickerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>How do you want to show up?</DialogTitle>
        <DialogContent>
          <FormControl component="fieldset" sx={{ mt: 1, width: '100%' }}>
            <FormLabel component="legend">Choose a starting presence</FormLabel>
            <RadioGroup
              value={capability}
              onChange={(e) => setCapability(e.target.value as PersonCapabilityKey)}
            >
              {CAPABILITY_OPTIONS.map((opt) => (
                <FormControlLabel
                  key={opt.value}
                  value={opt.value}
                  control={<Radio />}
                  label={
                    <Box sx={{ py: 0.5 }}>
                      <Typography variant="body1" fontWeight={600}>
                        {opt.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {opt.hint}
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>
          {error ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => void submitCreate()} disabled={creating}>
            {creating ? 'Creating…' : 'Create profile'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PeopleProfileCta;
export { PEOPLE_PROFILE_RETURN_KEY };
